// admin/components/LiveAgentChat.jsx
// ─────────────────────────────────────────────────────────────
// REWIRED against the Conversation-based backend. Key changes from
// the original:
//
// PROPS: was (ticket, roomId, socket, agent, onBack) — now
// (conversation, agent, onBack). No more roomId (the backend derives
// the room from the conversation's userId server-side — emitToUser()
// in services/supportEvents.js always targets support_${userId}, the
// client never needs to know/construct that string) and no more a
// socket prop threaded down from the parent — this component uses
// useSocket() directly, same shared connection AgentDashboard.jsx
// uses, instead of receiving someone else's socket instance as a prop.
//
// EVENTS:
//   loadChatHistory + chatHistory  → conversation IS the message list
//     already (conversation.messages), no separate fetch needed. The
//     parent already has the full conversation document (from
//     agentQueue or conversationOpened), so this component just reads
//     it directly on mount instead of round-tripping for history.
//   joinSupportChat                → agentClaim already did this
//     server-side (joins the user's room) when the agent claimed the
//     conversation in AgentDashboard.jsx. If this component is opened
//     for an ALREADY-claimed conversation (e.g. "My Conversations"
//     tab, agent revisiting one they already have), there's no
//     explicit re-join call — see gap note at the bottom.
//   send_message payload reshaped to match the new schema: role
//     ("user"/"agent"/"ai"/"system") instead of senderType
//     ("customer"/"agent"/"system"), type/text/fileUrl/fileName/
//     duration/senderId — matching models/Conversation.js's embedded
//     message schema exactly, not the old separate ChatMessage shape.
//   receive_message now arrives as { conversationId, message, tempId }
//     — destructure accordingly instead of receiving the message
//     fields directly on the event payload.
//   NEW: a "Close Conversation" action, calling agentCloseConversation
//     — the old dashboard had no equivalent (tickets presumably closed
//     some other way, e.g. an admin panel not shown). This is the
//     direct backend support for "the chat can then be ended by the
//     agent," per the original requirements for this feature.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSocket } from "../../../hooks/useSocket";

const API_BASE = import.meta.env.VITE_API_URL || "https://serve.easwallet.com";
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function LiveAgentChat({ conversation, agent, onBack }) {
  const [messages, setMessages]       = useState(conversation?.messages || []);
  const [convoStatus, setConvoStatus] = useState(conversation?.status);
  const [messageInput, setMessageInput] = useState("");
  const [typing, setTyping]           = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [closing, setClosing]         = useState(false);

  const chatRef        = useRef(null);
  const fileInputRef   = useRef(null);
  const messageIdRef   = useRef(0);
  const typingTimerRef = useRef(null);
  const isTypingRef    = useRef(false);

  const { connected, on, off, emit } = useSocket(agent.userId);

  // conversation.messages IS the chat history, already present on the
  // prop the moment this component mounts. The parent
  // (AgentDashboard.jsx) is responsible for sourcing an up-to-date
  // conversation object — either from agentQueue (pre-claim) or from
  // its OWN conversationOpened listener (post-claim, the authoritative
  // copy including the "agent joined" system message the claim itself
  // appends server-side). This component does NOT listen for
  // conversationOpened itself — it just trusts whatever conversation
  // object its parent hands it via props. State resets whenever that
  // object's _id changes (the `key={selectedConvo._id}` on this
  // component in AgentDashboard.jsx also forces a full remount per
  // conversation, so this effect is mostly a safety net for the case
  // where the conversation prop's CONTENT updates without the
  // component unmounting).
  useEffect(() => {
    setMessages(conversation?.messages || []);
    setConvoStatus(conversation?.status);
  }, [conversation?._id]);

  // FIXED — gap #4. agentClaim's room join only ever happened on the
  // socket instance live AT claim-time. If this component mounts for
  // a with_agent conversation via a DIFFERENT path (opened from "My
  // Conversations" after a page refresh / reconnect — i.e. NOT the
  // moment right after claiming it), the current socket was never
  // told to join that conversation's room, and would silently miss
  // every live event for it (new messages, typing, the user closing
  // their side, etc.) — see socketHandlers.js's agentRejoinConversation
  // for the full explanation. This explicitly (re)joins on mount
  // whenever the shown conversation is with_agent, regardless of
  // whether this is a fresh claim or a revisit. Harmless/idempotent
  // to call right after a fresh claim too (the room is simply
  // (re)joined again), so there's no need to special-case "did we
  // just claim this or not" — always rejoin on mount for an active
  // with_agent conversation.
  useEffect(() => {
    if (!connected || !conversation?._id || conversation.status !== "with_agent") return;
    emit("agentRejoinConversation", { conversationId: conversation._id });
  }, [connected, conversation?._id, conversation?.status, emit]);

  useEffect(() => {
    if (!connected || !conversation?.userId) return;

    function onReceiveMessage({ conversationId, message, tempId }) {
      if (conversationId !== conversation._id) return;

      setMessages(prev => {
        if (tempId) {
          const exists = prev.some(m => m._id === tempId);
          if (exists) return prev.map(m => (m._id === tempId ? message : m));
        }
        if (message._id && prev.some(m => m._id === message._id)) return prev;
        return [...prev, message];
      });
    }

    function onTyping({ conversationId }) {
      if (conversationId === conversation._id) setTyping(true);
    }
    function onStopTyping({ conversationId }) {
      if (conversationId === conversation._id) setTyping(false);
    }
    function onConversationClosed({ conversationId }) {
      if (conversationId === conversation._id) setConvoStatus("closed");
    }
    function onMessageError({ tempId }) {
      setMessages(prev => prev.filter(m => m._id !== tempId));
    }
    // FIXED — gap #4 cont. agentRejoinConversation's success response
    // — refresh this component's own messages/status from the
    // authoritative server copy, in case anything changed while
    // disconnected (e.g. the user sent a message that this socket's
    // OLD connection never received because it wasn't in the room yet).
    function onConversationOpened(convo) {
      if (convo._id !== conversation._id) return;
      setMessages(convo.messages || []);
      setConvoStatus(convo.status);
    }
    // FIXED — gap #4 cont. The rejoin was rejected — this
    // conversation isn't actually with_agent/assigned to this agent
    // anymore (someone else has it, or it got closed in the meantime
    // by another path). Reflect that locally instead of sitting in a
    // chat view that LOOKS live but is silently disconnected from
    // real updates.
    function onRejoinFailed({ conversationId, message }) {
      if (conversationId !== conversation._id) return;
      console.warn("[LiveAgentChat] rejoin failed:", message);
      setConvoStatus("closed");
    }

    on("receive_message", onReceiveMessage);
    on("typing", onTyping);
    on("stop_typing", onStopTyping);
    on("conversationClosed", onConversationClosed);
    on("message_error", onMessageError);
    on("conversationOpened", onConversationOpened);
    on("rejoinFailed", onRejoinFailed);

    return () => {
      off("receive_message", onReceiveMessage);
      off("typing", onTyping);
      off("stop_typing", onStopTyping);
      off("conversationClosed", onConversationClosed);
      off("message_error", onMessageError);
      off("conversationOpened", onConversationOpened);
      off("rejoinFailed", onRejoinFailed);
      clearTimeout(typingTimerRef.current);
    };
  }, [connected, conversation?._id, conversation?.userId, on, off]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const handleInputChange = (e) => {
    setMessageInput(e.target.value);
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      emit("typing", { conversationId: conversation._id, userId: conversation.userId });
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      emit("stop_typing", { conversationId: conversation._id, userId: conversation.userId });
    }, 1500);
  };

  const sendMessage = () => {
    if (!messageInput.trim() || convoStatus === "closed") return;
    clearTimeout(typingTimerRef.current);
    isTypingRef.current = false;
    emit("stop_typing", { conversationId: conversation._id, userId: conversation.userId });

    const tempId = `temp-${++messageIdRef.current}`;
    const text   = messageInput;

    setMessages(prev => [...prev, {
      _id: tempId, role: "agent", type: "text", text, at: Date.now(),
    }]);
    setMessageInput("");

    emit("send_message", {
      conversationId: conversation._id,
      role: "agent",
      type: "text",
      text,
      senderId: agent.userId,
      tempId,
    });
  };

  const uploadFile = async (file) => {
    if (convoStatus === "closed") return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(`"${file.name}" is too large. Maximum size is 10MB.`);
      return;
    }

    setUploadError("");
    const tempId   = `temp-${++messageIdRef.current}`;
    const isImage  = file.type.startsWith("image/");
    const isAudio  = file.type.startsWith("audio/");
    const msgType  = isAudio ? "audio" : isImage ? "image" : "document";

    setMessages(prev => [...prev, {
      _id: tempId, role: "agent", type: msgType,
      text: file.name || "Attachment", at: Date.now(),
    }]);

    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await axios.post(`${API_BASE}/api/mtrade/upload`, fd);
      if (!res.data?.url) throw new Error(res.data?.message || "Upload failed — no file URL returned");

      emit("send_message", {
        conversationId: conversation._id,
        role: "agent",
        type: msgType,
        fileUrl: res.data.url,
        fileName: file.name,
        text: file.name || "Attachment",
        senderId: agent.userId,
        tempId,
      });
    } catch (err) {
      setMessages(prev => prev.filter(m => m._id !== tempId));
      setUploadError(err.response?.data?.message || err.message || "Upload failed. Please try again.");
    }
  };

  // FIXED — new. The original had no close action exposed in this
  // component at all.
  const closeConversation = () => {
    if (convoStatus === "closed" || closing) return;
    setClosing(true);
    emit("agentCloseConversation", { conversationId: conversation._id });
    // convoStatus flips via the conversationClosed listener above once
    // the server confirms — not set optimistically here, since a
    // close should be confirmed by the server (it's a one-way door,
    // unlike sending a message which is safe to show optimistically).
  };

  const renderMessage = (msg) => {
    const isAgent  = msg.role === "agent";
    const isSystem = msg.role === "system";
    const isAi     = msg.role === "ai";
    const rawTime  = msg.at || msg.createdAt;
    const time     = rawTime
      ? new Date(rawTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "";

    if (isSystem) {
      return (
        <div key={msg._id} className="flex justify-center my-1">
          <span className="bg-white/10 text-white/40 text-xs px-4 py-1.5 rounded-full border border-white/10">
            {msg.text}
          </span>
        </div>
      );
    }

    return (
      <div key={msg._id} className={`flex items-end gap-2 ${isAgent ? "justify-end" : "justify-start"}`}>
        {!isAgent && (
          <div className="w-7 h-7 rounded-full bg-[#2a2a2a] border border-white/10 flex items-center justify-center text-xs text-white/60 flex-shrink-0">
            {isAi ? "🤖" : "U"}
          </div>
        )}

        <div className={`max-w-[75%] sm:max-w-[65%] px-4 py-3 rounded-2xl text-sm break-words ${
          isAgent
            ? "bg-[#00C896] text-black rounded-br-sm shadow-lg shadow-[#00C896]/20"
            : "bg-[#1e1e1e] text-white rounded-bl-sm border border-white/5"
        }`}>
          {!isAgent && isAi && <p className="text-[10px] font-semibold mb-1 text-[#00C896]">AI Assistant</p>}
          {msg.type === "text" && <p className="leading-relaxed whitespace-pre-wrap">{msg.text}</p>}
          {msg.type === "image" && <img src={msg.fileUrl} className="rounded-xl max-w-full" alt="attachment" />}
          {msg.type === "audio" && <audio controls src={msg.fileUrl} className="w-full max-w-[220px] h-8" />}
          {msg.type === "document" && (
            <a href={msg.fileUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2 flex items-center gap-2 break-all">
              📄 {msg.text}
            </a>
          )}
          <div className={`text-[10px] mt-1.5 text-right ${isAgent ? "text-black/50" : "text-white/30"}`}>{time}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0a0a]">
      <div className="px-4 sm:px-6 py-4 border-b border-white/8 bg-[#111111] flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button onClick={onBack} className="md:hidden w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors flex-shrink-0">
              <svg className="w-4 h-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {/* FIXED — avatar now shows the customer's first initial
              when their identity is known, instead of a generic
              static "U" regardless of who it actually is. */}
          <div className="w-9 h-9 rounded-full bg-[#00C896]/15 border border-[#00C896]/30 flex items-center justify-center text-[#00C896] font-bold flex-shrink-0">
            {(conversation.customer?.displayName || conversation.customer?.email || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            {/* FIXED — this is the actual fix for "so they know who is
                chatting." Was showing the conversation's raw _id as
                the PRIMARY header text, with category as secondary —
                an agent had no way to identify the customer from the
                header at all. Now the customer's display name (or
                email, or a fallback "User {id}" if no User record
                resolved) is the primary line; the conversation id +
                category move to the secondary line as supporting
                metadata, still visible but no longer the most
                prominent thing in the header. */}
            <h3 className="font-semibold text-white text-sm truncate">
              {conversation.customer?.displayName || conversation.customer?.email || `User ${conversation.userId?.slice(-8) || conversation._id.slice(-8)}`}
            </h3>
            <p className="text-xs text-white/40 truncate font-mono">
              #{conversation._id.slice(-8)}
              {conversation.escalation?.category && (
                <span className="font-sans"> · {conversation.escalation.category}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {convoStatus !== "closed" ? (
            <>
              <div className="w-2 h-2 bg-[#00C896] rounded-full animate-pulse" />
              <span className="text-xs text-[#00C896] font-medium mr-2">LIVE</span>
              <button
                onClick={closeConversation}
                disabled={closing}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/8 hover:bg-red-500/15 text-white/60 hover:text-red-400 transition-colors disabled:opacity-50"
              >
                {closing ? "Closing…" : "Close"}
              </button>
            </>
          ) : (
            <span className="text-xs text-white/30 font-medium">Closed</span>
          )}
        </div>
      </div>

      {/* FIXED — escalation summary panel, new. Surfaces exactly what
          the AI couldn't answer + its recap, per the original ask
          ("the agent will see everything... based on the last
          question the AI was not able to answer"). The old dashboard
          had no equivalent since the old backend never captured this. */}
      {conversation.escalation?.unresolvedQuestion && (
        <div className="mx-4 mt-3 px-4 py-3 bg-[#00C896]/8 border border-[#00C896]/20 rounded-xl flex-shrink-0">
          <p className="text-[10px] font-semibold text-[#00C896] uppercase tracking-wide mb-1">AI couldn't answer</p>
          <p className="text-sm text-white/90 mb-1.5">{conversation.escalation.unresolvedQuestion}</p>
          {conversation.escalation.summary && (
            <p className="text-xs text-white/50">{conversation.escalation.summary}</p>
          )}
        </div>
      )}

      {uploadError && (
        <div className="mx-4 mt-3 flex items-center gap-2 px-4 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs flex-shrink-0">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {uploadError}
          <button onClick={() => setUploadError("")} className="ml-auto text-red-400/60 hover:text-red-400">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div ref={chatRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
        {messages.length === 0 && (
          <div className="flex justify-center pt-10">
            <p className="text-white/20 text-sm">Start of conversation</p>
          </div>
        )}
        {messages.map((msg, i) => renderMessage({ ...msg, _id: msg._id || i }))}

        {typing && (
          <div className="flex items-end gap-2">
            <div className="w-7 h-7 rounded-full bg-[#2a2a2a] border border-white/10 flex items-center justify-center text-xs text-white/60 flex-shrink-0">U</div>
            <div className="bg-[#1e1e1e] border border-white/5 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center">
              <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
      </div>

      {convoStatus !== "closed" ? (
        <div className="p-3 sm:p-4 border-t border-white/8 bg-[#111111] flex-shrink-0">
          <div className={`flex items-center gap-2 rounded-2xl px-3 py-2 border transition-colors ${
            messageInput ? "bg-[#1a1a1a] border-[#00C896]/40" : "bg-[#1a1a1a] border-white/8"
          }`}>
            <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 hover:bg-white/8 rounded-xl flex items-center justify-center text-white/40 hover:text-white/70 transition-colors flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <input type="file" ref={fileInputRef} className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }} />

            <input
              type="text"
              value={messageInput}
              onChange={handleInputChange}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Type your reply…"
              className="flex-1 bg-transparent outline-none text-white placeholder-white/25 text-sm py-1 min-w-0"
            />

            <button
              onClick={sendMessage}
              disabled={!messageInput.trim()}
              className="w-9 h-9 bg-[#00C896] hover:bg-[#00b386] disabled:bg-white/8 disabled:text-white/20 text-black rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 border-t border-white/8 bg-[#111111] flex-shrink-0 text-center">
          <p className="text-white/30 text-sm">This conversation has been closed.</p>
        </div>
      )}
    </div>
  );
}

export default LiveAgentChat;
